/**
 * processOriginHighlight.js — E6 (historia 152, §8.8)
 *
 * El resaltado del grafo **por proceso de origen** (CA3.6, `RN-03.9`), en puro: de los nodos del
 * modelo a un color por `sourceProcessCode`, y de ese color a los nodos ya renderizados.
 *
 * Cuatro decisiones que este fichero fija:
 *
 * 1. **Un CUARTO canal visual** (§7.2, decisión nº 6, impacto R10). El nodo ya tiene tres canales
 *    ocupados —borde (selección, que manda, y resaltado azul de BOM) y fondo (nodo externo y
 *    relleno de los nodos WORKFLOW)—, así que el color de proceso viaja en `data.processOriginColor`
 *    y `nodes/TechnologyNode.jsx` lo pinta en una **banda propia**. Coexiste con los tres: la
 *    selección sigue mandando y nada se repinta.
 * 2. **Es una capa de LECTURA** (`IN14`). Se parchea `data` de los nodos YA renderizados —la misma
 *    técnica de `bomTree.js#applyJobHighlight`, y por la misma razón: reconstruirlos volvería a
 *    disparar `fitView` (pan/zoom)—; no se toca el modelo del editor, no se dispara nada al
 *    reducer y no se persiste ninguna preferencia.
 * 3. **El color se asigna POR CÓDIGO DE PROCESO y es estable durante la sesión** (`RN-03.9`): la
 *    asignación se hace por orden de aparición sobre una paleta fija y se **conserva** para los
 *    códigos ya vistos, de modo que añadir o quitar nodos no baraja los colores del resto.
 * 4. **Un nodo WORKFLOW se colorea por SU propio origen**, nunca por el de los nodos que contiene
 *    (`RN-01.9`, S88): el recorrido es del primer nivel —el que está en el lienzo— y no desciende
 *    a los subworkflows.
 * 5. *(D12, Enmienda 1)* **El origen es un PAR `(código, versión)` indivisible** (`RN-01.10b`,
 *    `IN16`): este fichero es el único que decide qué es un origen válido (`toSourceProcessPair`),
 *    y **medio par no lo es**. El **color** sigue agrupando por **CÓDIGO** y la **leyenda** lista
 *    **una entrada por PAR** (`RN-03.9`, S96): la versión **etiqueta**, no **colorea**.
 *
 * Los nodos **sin** proceso de origen no reciben color y la leyenda no los agrupa en ninguna
 * categoría inventada: se rotulan aparte con "Without source process" (J37).
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 */

/**
 * La paleta del resaltado. Colores propios, HARDCODED como los demás del editor
 * (`TechnologyNode.jsx`), para que el tema claro —cuyo primario es naranja— no los repinte, y
 * elegidos fuera de las familias ya usadas por los otros tres canales: el verde de la selección,
 * el azul `#4FC3F7` del resaltado de BOM y el morado `#6A1B9A` del nodo externo.
 */
export const PROCESS_ORIGIN_COLORS = [
  '#F5A623', // ámbar
  '#E056A0', // magenta
  '#8BC34A', // lima
  '#FF7043', // coral
  '#26C6DA', // turquesa
  '#B39DDB', // lavanda
  '#D4E157', // oliva
  '#FF8A80' // salmón
]

/**
 * S95 (D12, riesgo **R17**) — La versión de la PLANTILLA es un **decimal**: `1.5` es legal y
 * `parseInt`/`Math.round` la truncarían a `1`, que es otra versión. Se lee con `Number`, y todo lo
 * que no sea un número finito es **ausencia**, no un cero inventado.
 *
 * No confundir con la versión de la OPERACIÓN de librería, que es entera (`RN-01.10b` punto 3): son
 * dos números distintos que conviven en el mismo nodo.
 *
 * @param {number|string|null} value
 * @returns {number|null}
 */
export const toProcessVersionNumber = (value) => {
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }
  if (value === null || value === undefined) {
    return null
  }
  const versionNumber = Number(value)
  return Number.isFinite(versionNumber) ? versionNumber : null
}

/**
 * S94/S95 (`RN-01.10b` punto 1, `IN16`) — El **par indivisible** de origen, normalizado en el ÚNICO
 * sitio del cliente donde se decide qué es un origen válido: **o los dos campos o ninguno**. Medio
 * par no se manda (Sun lo rechazaría con `E-ORG-01`), no se pinta y no se colorea.
 *
 * @param {string} processCode Código de la plantilla RAÍZ del árbol arrastrado
 * @param {number|string|null} versionNumber Versión de ESA fila de plantilla
 * @returns {{sourceProcessCode: string, sourceProcessVersionNumber: number|null}}
 */
export const toSourceProcessPair = (processCode = '', versionNumber = null) => {
  const code = typeof processCode === 'string' ? processCode.trim() : ''
  const version = toProcessVersionNumber(versionNumber)
  if (!code || version === null) {
    return { sourceProcessCode: '', sourceProcessVersionNumber: null }
  }
  return { sourceProcessCode: code, sourceProcessVersionNumber: version }
}

/**
 * El par de origen de un nodo del modelo, ya normalizado: un nodo con **medio** par no tiene origen
 * (`IN16`), de modo que ni se colorea, ni entra en la leyenda, ni se rotula.
 *
 * @param {Node} node
 * @returns {{sourceProcessCode: string, sourceProcessVersionNumber: number|null}}
 */
export const nodeSourceProcessPair = (node = {}) =>
  toSourceProcessPair(node?.sourceProcessCode, node?.sourceProcessVersionNumber)

const originOf = (node) => nodeSourceProcessPair(node).sourceProcessCode

/**
 * S86/S88 — Los códigos de proceso presentes en el lienzo, **en orden de aparición** y sin
 * repetir. Solo el primer nivel: lo que se colorea es lo que se ve, y un subproceso vale por su
 * propio origen (S88).
 *
 * @param {Node[]} nodes Los nodos de primer nivel del modelo
 * @returns {string[]}
 */
export const collectProcessOriginCodes = (nodes = []) => {
  const codes = []
  ;(Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const processCode = originOf(node)
    if (processCode && !codes.includes(processCode)) {
      codes.push(processCode)
    }
  })
  return codes
}

/**
 * S86 — Un color por código de proceso. Los códigos ya conocidos **conservan el suyo** (esa es la
 * estabilidad de sesión que pide `RN-03.9`) y los nuevos toman el siguiente libre de la paleta,
 * que cicla si hay más procesos que colores.
 *
 * @param {string[]} processCodes Códigos en orden de aparición
 * @param {Object<string, string>} [knownColors] Lo ya asignado en esta sesión de edición
 * @returns {Object<string, string>}
 */
export const assignProcessOriginColors = (processCodes = [], knownColors = {}) => {
  const colors = { ...knownColors }
  let taken = Object.keys(colors).length
  ;(processCodes || []).forEach((processCode) => {
    if (!processCode || colors[processCode]) {
      return
    }
    colors[processCode] = PROCESS_ORIGIN_COLORS[taken % PROCESS_ORIGIN_COLORS.length]
    taken++
  })
  return colors
}

/**
 * S86/S88 — Qué color le toca a cada nodo del lienzo. Un nodo sin origen **no aparece** en el
 * índice: no se colorea (`RN-03.9`).
 *
 * @param {Node[]} nodes Los nodos de primer nivel del modelo
 * @param {Object<string, string>} colorByProcessCode
 * @returns {Object<string, string>}
 */
export const processOriginColorByNodeId = (nodes = [], colorByProcessCode = {}) => {
  const colorByNodeId = {}
  ;(Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const color = colorByProcessCode[originOf(node)]
    if (node?.id && color) {
      colorByNodeId[node.id] = color
    }
  })
  return colorByNodeId
}

/**
 * S86/S87 — Aplica el cuarto canal sobre los nodos YA RENDERIZADOS. Un nodo sin color asignado
 * queda con `processOriginColor: ''` —que es como se apaga el resaltado y como el nodo vuelve
 * exactamente a su presentación normal (S87)—, y si no hay nada que cambiar se devuelve **el mismo
 * array**, para no provocar un repintado del lienzo por gusto.
 *
 * @param {import('reactflow').Node[]} nodes
 * @param {Object<string, string>} colorByNodeId
 * @returns {import('reactflow').Node[]}
 */
export const applyProcessOriginHighlight = (nodes = [], colorByNodeId = {}) => {
  let changed = false
  const painted = (nodes || []).map((node) => {
    const color = colorByNodeId[node?.id] || ''
    if ((node?.data?.processOriginColor || '') === color) {
      return node
    }
    changed = true
    return { ...node, data: { ...node.data, processOriginColor: color } }
  })
  return changed ? painted : nodes
}

/**
 * S86/S88/S96 — Lo que la leyenda (E7) tiene que decir: **una entrada por PAR
 * `(código, versión)` presente** en el lienzo, si hay nodos que **no** se colorean (J37) y si hay
 * algún origen en absoluto.
 *
 * *(D12)* Las entradas van **por par y el color por código**: dos nodos traídos de dos versiones
 * distintas de la misma plantilla dan **dos entradas del MISMO color** (S96) —la versión ETIQUETA,
 * no COLOREA—, y un tercer nodo del par que ya está listado **no añade entrada**: la leyenda lista
 * pares presentes, no nodos. El orden es el de aparición, como el de los colores.
 *
 * Sin ningún origen, el interruptor se puede encender igual: no colorea nada y la leyenda **lo
 * declara**, en vez de inventarse una categoría o colorear el grafo entero (S88).
 *
 * @param {Node[]} nodes Los nodos de primer nivel del modelo
 * @param {Object<string, string>} colorByProcessCode
 * @returns {{ entries: {processCode: string, versionNumber: number|null, color: string}[],
 *   hasNodesWithoutOrigin: boolean, anyOrigin: boolean }}
 */
export const buildProcessOriginLegend = (nodes = [], colorByProcessCode = {}) => {
  const entries = []
  const seen = new Set()
  ;(Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const { sourceProcessCode, sourceProcessVersionNumber } = nodeSourceProcessPair(node)
    if (!sourceProcessCode) {
      return
    }
    const pairKey = sourceProcessCode + ' v' + sourceProcessVersionNumber
    if (seen.has(pairKey)) {
      return
    }
    seen.add(pairKey)
    entries.push({
      processCode: sourceProcessCode,
      versionNumber: sourceProcessVersionNumber,
      color: colorByProcessCode[sourceProcessCode] || ''
    })
  })
  return {
    entries,
    hasNodesWithoutOrigin: (Array.isArray(nodes) ? nodes : []).some((node) => !originOf(node)),
    anyOrigin: entries.length > 0
  }
}
